import { fnv1a64 } from './similarity'
import { canonicalSemanticJson } from './semanticGit'
import {
  checkMathCertificate,
  type MathCertificate,
  type MathCertificateResult,
} from './mathCertificates'

/** Mathematical records have their own authority. They never write a web
 * verification, taint the exploratory canvas, or inherit source-count verdicts. */
export interface MathRef {
  id: string
  revision: string
}
export interface MathRevision extends MathRef {
  createdAt: number
}
export type MathClaimKind =
  | 'general'
  | 'feasibility-implies-satisfiability'
  | 'universal-completion'
  | 'instance-feasible'
  | 'instance-infeasible'
  | 'boolean-satisfiable'
  | 'boolean-unsatisfiable'

export interface ConjecturePackage extends MathRevision {
  title: string
  statement: string
  quantifiers: string
  assumptions: string[]
  encoding: string
  rules: string[]
  proofLength: string
  relaxation: string
  parameters: string
  artifacts: MathRef[]
  parent: MathRef | null
  repair: {
    defectEvidenceIds: string[]
    explanation: string
    preservation: MathRef
    complexity: MathRef
  } | null
}
export interface ProofObligation extends MathRevision {
  /** The arithmetic and its interpretation are reviewed separately. */
  certificateBinding?: { target: MathRef; certificateRevision: string }
  nodeId: string
  title: string
  statement: string
  claimKind: MathClaimKind
  assumptions: string[]
  package: MathRef
  requires: MathRef[]
  steps: { id: string; text: string }[]
}
export interface MathRelationship {
  id: string
  from: MathRef
  to: MathRef
  kind:
    'sufficient-condition' | 'equivalence' | 'auxiliary-lemma' | 'proof-mechanism' | 'reformulation'
  argument: string
  /** A relationship is a recorded claim until its own obligation is usable. */
  justification: MathRef | null
}
export interface ReusableTheorem extends MathRevision {
  title: string
  source: {
    locator: string
    version: string
    theorem: string
    /** The exact retained source revision, not a claim that a URL was searched. */
    anchor: { nodeId: string; revision: string } | null
  }
  statement: string
  hypotheses: { id: string; statement: string }[]
}
export type HypothesisMapping = {
  hypothesisId: string
  reason: string
  via: { kind: 'assumption'; text: string } | { kind: 'obligation'; ref: MathRef }
}
export const MATH_REVIEWER_KINDS = ['human', 'ai', 'unspecified'] as const
export type MathReviewerKind = (typeof MATH_REVIEWER_KINDS)[number]

/** Attribution is a declaration, never an identity check or a proof authority. */
export function mathReviewerLabel(kind?: MathReviewerKind) {
  return kind === 'human'
    ? 'human review'
    : kind === 'ai'
      ? 'AI review'
      : 'review (reviewer type unspecified)'
}

interface EvidenceBase {
  id: string
  target: MathRef
  package: MathRef
  createdAt: number
  note: string
  /** Absent in historical records; a name alone cannot identify a human. */
  reviewerKind?: MathReviewerKind
}
export type MathematicalEvidence = EvidenceBase &
  (
    | {
        kind: 'derivation-review'
        reviewer: string
        disposition: 'accepted' | 'unsupported'
        reviewedSteps: string[]
        firstUnsupportedStep: string | null
        reasoning: string
      }
    | {
        kind: 'theorem-application'
        theorem: MathRef
        reviewer: string
        mappings: HypothesisMapping[]
        translation: {
          notation: string
          encoding: string
          proofConvention: string
          consequence: string
        }
      }
    | {
        kind: 'exact-certificate'
        certificate: unknown
        interpretation: {
          obligation?: MathRef | null
          instance: string
          reviewer: string
          explanation: string
          artifacts: MathRef[]
        }
      }
  )
export type DiagnosticRole =
  'target' | 'implementation-validation' | 'generic-control' | 'repair-regression' | 'exploration'
export interface DiagnosticPlan extends MathRevision {
  target: MathRef
  package: MathRef
  role: DiagnosticRole
  question: string
  supportingPurpose: string
  experimentId: string | null
  experimentRevision?: string | null
  requires: MathRef[]
  outcomes: {
    name: string
    effect:
      | 'may-refute-target'
      | 'settles-instance'
      | 'tests-mechanism'
      | 'validates-implementation'
      | 'inconclusive'
    certificate: MathCertificate['kind'] | null
    explanation: string
  }[]
}
export interface MathRegressionRun {
  id: string
  package: MathRef
  defectEvidenceId: string | null
  kind: 'exclude-counterexample' | 'preserve-valid-case'
  certificate: unknown
  inputArtifacts: MathRef[]
  interpretation: string
  createdAt: number
}
export interface MathematicalResearch {
  version: 1
  enabled: boolean
  subject: 'general' | 'mathematics'
  focus: MathRef | null
  packages: ConjecturePackage[]
  obligations: ProofObligation[]
  relationships: MathRelationship[]
  theorems: ReusableTheorem[]
  evidence: MathematicalEvidence[]
  plans: DiagnosticPlan[]
  regressions: MathRegressionRun[]
}
// A long investigation can retain many short obligations. Keep that history
// separate from the size of one proof's steps or assumptions, and keep the
// whole-workspace byte limit as the outer bound on every imported collection.
export const MATH_RESEARCH_LIMITS = {
  bytes: 6_000_000,
  rows: 512,
  ledgerRows: 4096,
  text: 120_000,
} as const
export const MATH_CLAIM_KINDS: MathClaimKind[] = [
  'general',
  'feasibility-implies-satisfiability',
  'universal-completion',
  'instance-feasible',
  'instance-infeasible',
  'boolean-satisfiable',
  'boolean-unsatisfiable',
]
export const MATH_RELATION_KINDS: MathRelationship['kind'][] = [
  'sufficient-condition',
  'equivalence',
  'auxiliary-lemma',
  'proof-mechanism',
  'reformulation',
]
export const MATH_DIAGNOSTIC_ROLES: DiagnosticRole[] = [
  'target',
  'implementation-validation',
  'generic-control',
  'repair-regression',
  'exploration',
]
export const mathRef = ({ id, revision }: MathRef): MathRef => ({ id, revision })
export const mathCertificateRevision = (certificate: unknown): string =>
  fnv1a64(canonicalSemanticJson(certificate))

export function certificateInterpretationObligation(
  target: ProofObligation,
  certificate: unknown,
  instance: string,
  explanation: string,
  id: string,
  createdAt: number,
): ProofObligation {
  return mathRevision({
    id,
    createdAt,
    nodeId: target.nodeId,
    title: `Interpretation: ${target.title}`,
    statement: `The exact certificate for ${instance} applies to ${target.statement}`,
    claimKind: 'general' as const,
    assumptions: [...target.assumptions],
    package: target.package,
    requires: [],
    certificateBinding: {
      target: mathRef(target),
      certificateRevision: mathCertificateRevision(certificate),
    },
    steps: [
      { id: 'instance-membership', text: explanation },
      {
        id: 'target-assumptions',
        text: `Check the exact encoding, proof conventions and every target assumption: ${target.assumptions.join('; ') || 'none declared'}.`,
      },
    ],
  })
}
export const sameMathRef = (a: MathRef | null | undefined, b: MathRef | null | undefined) =>
  !!a && !!b && a.id === b.id && a.revision === b.revision
export function mathRevision<T extends object>(body: T): T & { revision: string } {
  const { revision: _ignored, ...content } = body as T & { revision?: string }
  return { ...content, revision: fnv1a64(canonicalSemanticJson(content)) } as T & {
    revision: string
  }
}
export const mathHead = <T extends MathRef>(rows: readonly T[], id: string): T | undefined =>
  rows.findLast((r) => r.id === id)
export const mathHeads = <T extends MathRef>(rows: readonly T[]): T[] =>
  rows.filter((r, i) => rows.findLastIndex((v) => v.id === r.id) === i)
const revisionOf = <T extends MathRef>(rows: readonly T[], ref: MathRef) =>
  rows.find((r) => sameMathRef(r, ref))
const current = <T extends MathRef>(rows: readonly T[], ref: MathRef) =>
  sameMathRef(mathHead(rows, ref.id), ref)
export function createMathematicalResearch(): MathematicalResearch {
  return {
    version: 1,
    enabled: false,
    subject: 'general',
    focus: null,
    packages: [],
    obligations: [],
    relationships: [],
    theorems: [],
    evidence: [],
    plans: [],
    regressions: [],
  }
}

const obj = (v: unknown): v is Record<string, unknown> =>
  !!v && typeof v === 'object' && !Array.isArray(v)
const text = (v: unknown, empty = false): v is string =>
  typeof v === 'string' && (empty || v.trim().length > 0) && v.length <= MATH_RESEARCH_LIMITS.text
const id = (v: unknown): v is string => text(v) && v.length <= 240
const ref = (v: unknown): v is MathRef => obj(v) && id(v.id) && id(v.revision)
const list = <T>(v: unknown, test: (item: unknown) => item is T): v is T[] =>
  Array.isArray(v) && v.length <= MATH_RESEARCH_LIMITS.rows && v.every(test)
const ledger = <T>(v: unknown, test: (item: unknown) => item is T): v is T[] =>
  Array.isArray(v) && v.length <= MATH_RESEARCH_LIMITS.ledgerRows && v.every(test)
const strings = (v: unknown): v is string[] => list(v, (x): x is string => text(x))
const refs = (v: unknown): v is MathRef[] => list(v, ref)
const time = (v: unknown) => typeof v === 'number' && Number.isFinite(v) && v >= 0
const revised = (v: unknown): v is Record<string, unknown> & MathRevision =>
  obj(v) && ref(v) && time(v.createdAt) && mathRevision(v).revision === v.revision
const steps = (v: unknown): v is { id: string; text: string }[] =>
  list(v, (x): x is { id: string; text: string } => obj(x) && id(x.id) && text(x.text)) &&
  new Set(v.map((x) => x.id)).size === v.length
const packageRow = (v: unknown): v is ConjecturePackage =>
  revised(v) &&
  [
    'title',
    'statement',
    'quantifiers',
    'encoding',
    'proofLength',
    'relaxation',
    'parameters',
  ].every((key) => text(v[key])) &&
  strings(v.assumptions) &&
  strings(v.rules) &&
  refs(v.artifacts) &&
  (v.parent === null || ref(v.parent)) &&
  (v.repair === null ||
    (obj(v.repair) &&
      strings(v.repair.defectEvidenceIds) &&
      v.repair.defectEvidenceIds.length > 0 &&
      text(v.repair.explanation) &&
      ref(v.repair.preservation) &&
      ref(v.repair.complexity)))
const obligationRow = (v: unknown): v is ProofObligation =>
  revised(v) &&
  (v.certificateBinding === undefined ||
    (obj(v.certificateBinding) &&
      ref(v.certificateBinding.target) &&
      id(v.certificateBinding.certificateRevision))) &&
  id(v.nodeId) &&
  text(v.title) &&
  text(v.statement) &&
  MATH_CLAIM_KINDS.includes(v.claimKind as MathClaimKind) &&
  strings(v.assumptions) &&
  ref(v.package) &&
  refs(v.requires) &&
  steps(v.steps)
const relationshipRow = (v: unknown): v is MathRelationship =>
  obj(v) &&
  id(v.id) &&
  ref(v.from) &&
  ref(v.to) &&
  MATH_RELATION_KINDS.includes(v.kind as MathRelationship['kind']) &&
  text(v.argument) &&
  (v.justification === null || ref(v.justification))
const theoremRow = (v: unknown): v is ReusableTheorem =>
  revised(v) &&
  text(v.title) &&
  text(v.statement) &&
  obj(v.source) &&
  ['locator', 'version', 'theorem'].every((key) =>
    text(v.source && (v.source as Record<string, unknown>)[key]),
  ) &&
  (v.source.anchor === null ||
    (obj(v.source.anchor) && id(v.source.anchor.nodeId) && id(v.source.anchor.revision))) &&
  list(
    v.hypotheses,
    (h): h is { id: string; statement: string } => obj(h) && id(h.id) && text(h.statement),
  ) &&
  new Set(v.hypotheses.map((h) => h.id)).size === v.hypotheses.length
const mapping = (v: unknown): v is HypothesisMapping =>
  obj(v) &&
  id(v.hypothesisId) &&
  text(v.reason) &&
  obj(v.via) &&
  ((v.via.kind === 'assumption' && text(v.via.text)) ||
    (v.via.kind === 'obligation' && ref(v.via.ref)))
const evidenceRow = (v: unknown): v is MathematicalEvidence => {
  if (
    !obj(v) ||
    !id(v.id) ||
    !ref(v.target) ||
    !ref(v.package) ||
    !time(v.createdAt) ||
    !text(v.note, true) ||
    (v.reviewerKind !== undefined &&
      !MATH_REVIEWER_KINDS.includes(v.reviewerKind as MathReviewerKind))
  )
    return false
  if (v.kind === 'derivation-review')
    return (
      text(v.reviewer) &&
      ['accepted', 'unsupported'].includes(String(v.disposition)) &&
      strings(v.reviewedSteps) &&
      (v.firstUnsupportedStep === null || id(v.firstUnsupportedStep)) &&
      text(v.reasoning)
    )
  if (v.kind === 'theorem-application')
    return (
      ref(v.theorem) &&
      text(v.reviewer) &&
      list(v.mappings, mapping) &&
      obj(v.translation) &&
      ['notation', 'encoding', 'proofConvention', 'consequence'].every((key) =>
        text((v.translation as Record<string, unknown>)[key]),
      )
    )
  if (v.kind === 'exact-certificate')
    return (
      obj(v.certificate) &&
      obj(v.interpretation) &&
      (v.interpretation.obligation == null || ref(v.interpretation.obligation)) &&
      ['instance', 'reviewer', 'explanation'].every((key) =>
        text((v.interpretation as Record<string, unknown>)[key]),
      ) &&
      refs(v.interpretation.artifacts)
    )
  return false
}
const planRow = (v: unknown): v is DiagnosticPlan =>
  revised(v) &&
  ref(v.target) &&
  ref(v.package) &&
  MATH_DIAGNOSTIC_ROLES.includes(v.role as DiagnosticRole) &&
  text(v.question) &&
  text(v.supportingPurpose, v.role === 'target') &&
  (v.experimentId === null || id(v.experimentId)) &&
  (v.experimentRevision == null || id(v.experimentRevision)) &&
  refs(v.requires) &&
  list(
    v.outcomes,
    (o): o is DiagnosticPlan['outcomes'][number] =>
      obj(o) &&
      id(o.name) &&
      [
        'may-refute-target',
        'settles-instance',
        'tests-mechanism',
        'validates-implementation',
        'inconclusive',
      ].includes(String(o.effect)) &&
      (o.certificate === null ||
        [
          'linear-feasible',
          'linear-infeasible',
          'boolean-satisfiable',
          'boolean-feasible',
          'boolean-unsatisfiable',
          'relaxation-gap',
          'nonrepresentable-point',
        ].includes(String(o.certificate))) &&
      text(o.explanation),
  ) &&
  v.outcomes.length > 0
const regressionRow = (v: unknown): v is MathRegressionRun =>
  obj(v) &&
  id(v.id) &&
  ref(v.package) &&
  (v.defectEvidenceId === null || id(v.defectEvidenceId)) &&
  ['exclude-counterexample', 'preserve-valid-case'].includes(String(v.kind)) &&
  obj(v.certificate) &&
  refs(v.inputArtifacts) &&
  text(v.interpretation) &&
  time(v.createdAt)

/** No imported status is trusted. Known invalid certificates stay inspectable;
 * malformed structure refuses the whole workspace rather than dropping a blocker. */
export function parseMathematicalResearch(value: unknown): MathematicalResearch | null {
  try {
    const v: unknown = JSON.parse(JSON.stringify(value))
    if (
      !obj(v) ||
      JSON.stringify(v).length > MATH_RESEARCH_LIMITS.bytes ||
      v.version !== 1 ||
      typeof v.enabled !== 'boolean' ||
      !['general', 'mathematics'].includes(String(v.subject)) ||
      (v.focus !== null && !ref(v.focus))
    )
      return null
    if (
      !ledger(v.packages, packageRow) ||
      !ledger(v.obligations, obligationRow) ||
      !ledger(v.relationships, relationshipRow) ||
      !ledger(v.theorems, theoremRow) ||
      !ledger(v.evidence, evidenceRow) ||
      !ledger(v.plans, planRow) ||
      !ledger(v.regressions, regressionRow)
    )
      return null
    for (const rows of [v.packages, v.obligations, v.theorems, v.plans])
      if (new Set(rows.map((r) => `${r.id}:${r.revision}`)).size !== rows.length) return null
    for (const rows of [v.relationships, v.evidence, v.regressions])
      if (new Set(rows.map((r) => r.id)).size !== rows.length) return null
    const state: MathematicalResearch = {
      version: 1,
      enabled: v.enabled,
      subject: v.subject as MathematicalResearch['subject'],
      focus: v.focus,
      packages: v.packages,
      obligations: v.obligations,
      relationships: v.relationships,
      theorems: v.theorems,
      evidence: v.evidence,
      plans: v.plans,
      regressions: v.regressions,
    }
    return state
  } catch {
    return null
  }
}

export interface MathContext {
  /** Current source and artifact fingerprints come from the actual session. */
  sourceRevisions?: Record<string, string>
  artifactRevisions?: Record<string, string>
}
export type MathObligationStatus =
  'unresolved' | 'blocked' | 'stale' | 'reviewed' | 'certificate-checked' | 'counterexample-checked'
export interface MathObligationAssessment {
  obligation: ProofObligation
  status: MathObligationStatus
  reasons: string[]
  evidenceIds: string[]
}
const usable = (status: MathObligationStatus) =>
  status === 'reviewed' || status === 'certificate-checked'
export interface MathEvidenceAssessment {
  id: string
  effect:
    | 'establishes-instance'
    | 'reviewed-argument'
    | 'refutes-target'
    | 'context'
    | 'unsupported'
    | 'invalid'
    | 'stale'
  reason: string
  certificate?: MathCertificateResult
}
const refsMatch = (wanted: MathRef[], available: MathRef[]) =>
  wanted.every((r) => available.some((a) => sameMathRef(r, a)))

export function assessMathematicalResearch(state: MathematicalResearch, context: MathContext = {}) {
  const memo = new Map<string, MathObligationAssessment>()
  const evidence = new Map<string, MathEvidenceAssessment>()
  const assess = (reference: MathRef, stack: string[] = []): MathObligationAssessment | null => {
    const o = revisionOf(state.obligations, reference)
    if (!o) return null
    const key = `${o.id}:${o.revision}`
    const found = memo.get(key)
    if (found) return found
    const answer = (
      status: MathObligationStatus,
      reasons: string[],
      evidenceIds: string[] = [],
    ) => {
      const result = { obligation: o, status, reasons, evidenceIds }
      memo.set(key, result)
      return result
    }
    if (stack.includes(key))
      return {
        obligation: o,
        status: 'blocked',
        reasons: ['Cyclic proof dependency.'],
        evidenceIds: [],
      }
    const next = [...stack, key]
    const pkg = revisionOf(state.packages, o.package)
    if (!current(state.obligations, o) || !pkg || !current(state.packages, o.package))
      return answer('stale', ['The obligation or its canonical conjecture package changed.'])
    if (
      context.artifactRevisions &&
      pkg.artifacts.some((r) => context.artifactRevisions![r.id] !== r.revision)
    )
      return answer('stale', ['A canonical input artifact changed or is unavailable.'])
    const blocked = o.requires.flatMap((r) => {
      const dep = assess(r, next)
      return dep && usable(dep.status) ? [] : [`Dependency ${r.id} is ${dep?.status ?? 'missing'}.`]
    })
    const evaluations = state.evidence
      .filter((e) => sameMathRef(e.target, o))
      .map((e): MathEvidenceAssessment => {
        const result = (
          effect: MathEvidenceAssessment['effect'],
          reason: string,
          certificate?: MathCertificateResult,
        ) => {
          const value = { id: e.id, effect, reason, ...(certificate ? { certificate } : {}) }
          evidence.set(e.id, value)
          return value
        }
        if (!sameMathRef(e.package, o.package))
          return result('stale', 'Evidence belongs to a different conjecture revision.')
        if (e.kind === 'derivation-review') {
          const ids = o.steps.map((step) => step.id)
          if (e.disposition === 'unsupported')
            return ids.includes(e.firstUnsupportedStep ?? '')
              ? result(
                  'unsupported',
                  `Recorded review identifies unsupported step ${e.firstUnsupportedStep}: ${e.reasoning}`,
                )
              : result('invalid', 'An objection must identify an existing proof step.')
          if (
            !ids.length ||
            e.firstUnsupportedStep !== null ||
            ids.length !== new Set(e.reviewedSteps).size ||
            !ids.every((id) => e.reviewedSteps.includes(id))
          )
            return result(
              'invalid',
              'An accepted derivation review must cover every retained proof step.',
            )
          return result(
            'reviewed-argument',
            `Recorded ${mathReviewerLabel(e.reviewerKind)} by ${e.reviewer}; not formal certification or authenticated identity.`,
          )
        }
        if (e.kind === 'theorem-application') {
          const theorem = revisionOf(state.theorems, e.theorem)
          if (!theorem || !current(state.theorems, e.theorem))
            return result('stale', 'The theorem or its source version changed.')
          if (
            theorem.source.anchor &&
            (!context.sourceRevisions ||
              context.sourceRevisions[theorem.source.anchor.nodeId] !==
                theorem.source.anchor.revision)
          )
            return result('stale', 'The retained source changed or cannot be reconstructed.')
          if (
            theorem.hypotheses.length !== e.mappings.length ||
            new Set(e.mappings.map((m) => m.hypothesisId)).size !== e.mappings.length
          )
            return result('invalid', 'Every theorem hypothesis needs exactly one translation.')
          for (const h of theorem.hypotheses) {
            const m = e.mappings.find((r) => r.hypothesisId === h.id)
            if (!m) return result('invalid', `Unmatched theorem hypothesis ${h.id}.`)
            if (m.via.kind === 'assumption') {
              if (!o.assumptions.includes(m.via.text))
                return result('invalid', `Hypothesis ${h.id} is not an explicit target assumption.`)
            } else {
              const dep = assess(m.via.ref, next)
              if (!dep || !usable(dep.status))
                return result(
                  'stale',
                  `Hypothesis ${h.id} depends on an unresolved or changed obligation.`,
                )
            }
          }
          return result(
            'reviewed-argument',
            `Recorded theorem application ${mathReviewerLabel(e.reviewerKind)} by ${e.reviewer}; source, hypotheses and convention translation retained.`,
          )
        }
        const certificate = checkMathCertificate(e.certificate)
        if (!certificate.valid) return result('invalid', certificate.reason, certificate)
        if (
          !refsMatch(e.interpretation.artifacts, pkg.artifacts) ||
          !refsMatch(pkg.artifacts, e.interpretation.artifacts)
        )
          return result(
            'stale',
            'Certificate interpretation must bind all canonical input artifacts.',
            certificate,
          )
        const fact = certificate.fact
        const scope = e.interpretation.obligation
          ? revisionOf(state.obligations, e.interpretation.obligation)
          : null
        if (
          !scope ||
          !sameMathRef(scope.package, o.package) ||
          !sameMathRef(scope.certificateBinding?.target, o) ||
          scope.certificateBinding?.certificateRevision !==
            mathCertificateRevision(e.certificate) ||
          !usable(assess(scope, next)?.status ?? 'unresolved')
        )
          return result(
            'context',
            'The finite certificate checks out. A current, separately reviewed obligation must establish its exact instance interpretation and every target assumption before it can settle this target.',
            certificate,
          )
        if (
          (o.claimKind === 'feasibility-implies-satisfiability' && fact === 'relaxation-gap') ||
          (o.claimKind === 'universal-completion' && fact === 'nonrepresentable-point')
        )
          return result(
            'refutes-target',
            'Exact counterexample checked under the recorded instance interpretation; it affects this target only.',
            certificate,
          )
        const instanceFacts: Partial<Record<MathClaimKind, MathCertificate['kind']>> = {
          'instance-feasible': 'linear-feasible',
          'instance-infeasible': 'linear-infeasible',
          'boolean-satisfiable': 'boolean-satisfiable',
          'boolean-unsatisfiable': 'boolean-unsatisfiable',
        }
        if (instanceFacts[o.claimKind] === fact)
          return result(
            'establishes-instance',
            'Exact instance result checked under the recorded interpretation.',
            certificate,
          )
        return result(
          'context',
          'The certificate checks a different logical claim; this obligation remains unresolved.',
          certificate,
        )
      })
    // A checked counterexample need not prove the premises of an abandoned proof
    // strategy. It is tied to the target assumptions and its own interpretation.
    const refutations = evaluations.filter((e) => e.effect === 'refutes-target')
    if (refutations.length)
      return answer(
        'counterexample-checked',
        refutations.map((e) => e.reason),
        refutations.map((e) => e.id),
      )
    if (blocked.length) return answer('blocked', blocked)
    if (evaluations.some((e) => e.effect === 'unsupported'))
      return answer(
        'unresolved',
        evaluations.filter((e) => e.effect === 'unsupported').map((e) => e.reason),
      )
    const certificates = evaluations.filter((e) => e.effect === 'establishes-instance')
    if (certificates.length)
      return answer(
        'certificate-checked',
        certificates.map((e) => e.reason),
        certificates.map((e) => e.id),
      )
    const reviews = evaluations.filter((e) => e.effect === 'reviewed-argument')
    if (reviews.length)
      return answer(
        'reviewed',
        reviews.map((e) => e.reason),
        reviews.map((e) => e.id),
      )
    return answer(
      'unresolved',
      evaluations.length
        ? evaluations.map((e) => e.reason)
        : ['No applicable checked certificate or complete recorded review.'],
    )
  }
  const obligations = mathHeads(state.obligations).map((o) => assess(o)!)
  const relationships = state.relationships.map((r) => ({
    ...r,
    current: current(state.obligations, r.from) && current(state.obligations, r.to),
    justified: r.justification !== null && usable(assess(r.justification)?.status ?? 'unresolved'),
    mechanismEliminated:
      r.kind === 'proof-mechanism' &&
      current(state.obligations, r.from) &&
      current(state.obligations, r.to) &&
      assess(r.from)?.status === 'counterexample-checked',
  }))
  return {
    obligations,
    evidence: [...evidence.values()],
    relationships,
    usable: obligations.filter((o) => usable(o.status)).length,
    counterexamples: obligations.filter((o) => o.status === 'counterexample-checked').length,
    blocked: obligations.filter((o) => ['blocked', 'stale'].includes(o.status)).length,
    unresolved: obligations.filter((o) => o.status === 'unresolved').length,
    mechanismsEliminated: relationships.filter((r) => r.mechanismEliminated).length,
    reusedDependencies: obligations.reduce(
      (n, o) =>
        n + o.obligation.requires.filter((r) => usable(assess(r)?.status ?? 'unresolved')).length,
      0,
    ),
    theoremApplications: [...evidence.values()].filter(
      (e) =>
        e.effect === 'reviewed-argument' &&
        state.evidence.find((row) => row.id === e.id)?.kind === 'theorem-application',
    ).length,
  }
}

export function mathematicalDependencyIssue(
  state: MathematicalResearch,
  references: MathRef[],
  context: MathContext = {},
): string | null {
  const assessment = assessMathematicalResearch(state, context)
  for (const reference of references) {
    const row = assessment.obligations.find((o) => sameMathRef(o.obligation, reference))
    if (!row || !usable(row.status))
      return `Mathematical dependency ${reference.id} is ${row?.status ?? 'missing or superseded'}; completing an action does not discharge it.`
  }
  return null
}

export function diagnosticPlanIssue(
  state: MathematicalResearch,
  plan: DiagnosticPlan,
  context: MathContext = {},
): string | null {
  if (
    !current(state.plans, plan) ||
    !current(state.obligations, plan.target) ||
    !current(state.packages, plan.package)
  )
    return 'The diagnostic plan or its target inputs changed.'
  const obligation = revisionOf(state.obligations, plan.target)!
  if (!sameMathRef(obligation.package, plan.package))
    return 'The experiment uses a different conjecture package.'
  const missing = mathematicalDependencyIssue(
    state,
    [...obligation.requires, ...plan.requires],
    context,
  )
  if (missing) return missing
  if (!plan.outcomes.some((o) => o.effect === 'inconclusive' && o.certificate === null))
    return 'Declare an inconclusive outcome for timeout, error or missing exact evidence.'
  if (plan.role !== 'target' && !plan.supportingPurpose.trim())
    return 'An adjacent experiment needs an explicit supporting purpose.'
  for (const outcome of plan.outcomes) {
    if (outcome.effect === 'may-refute-target') {
      if (plan.role !== 'target') return 'An adjacent experiment cannot claim to refute the target.'
      const wanted =
        obligation.claimKind === 'feasibility-implies-satisfiability'
          ? 'relaxation-gap'
          : obligation.claimKind === 'universal-completion'
            ? 'nonrepresentable-point'
            : null
      if (!wanted || outcome.certificate !== wanted)
        return 'The proposed counterexample does not match the target quantifiers.'
    }
    if (outcome.effect !== 'inconclusive' && !outcome.explanation.trim())
      return 'Every informative outcome needs its exact consequence.'
  }
  return null
}

export const diagnosticPriority = (role: DiagnosticRole) =>
  ({
    target: 0,
    'repair-regression': 1,
    'implementation-validation': 2,
    'generic-control': 3,
    exploration: 4,
  })[role]

export function checkMathRegression(state: MathematicalResearch, run: MathRegressionRun) {
  const pkg = revisionOf(state.packages, run.package)
  if (
    !pkg ||
    !refsMatch(pkg.artifacts, run.inputArtifacts) ||
    !refsMatch(run.inputArtifacts, pkg.artifacts)
  )
    return { passed: false, reason: 'Regression inputs do not match this canonical package.' }
  const result = checkMathCertificate(run.certificate)
  if (result.failure === 'malformed') return { passed: false, reason: result.reason }
  const defect = state.evidence.find((e) => e.id === run.defectEvidenceId)
  if (
    !pkg.parent ||
    !defect ||
    defect.kind !== 'exact-certificate' ||
    !sameMathRef(defect.package, pkg.parent) ||
    !checkMathCertificate(defect.certificate).valid
  )
    return {
      passed: false,
      reason: 'The original checked case is missing or belongs to another parent package.',
    }

  const old = defect.certificate as MathCertificate
  const updated = run.certificate as MathCertificate
  if (
    run.kind === 'exclude-counterexample' &&
    !['relaxation-gap', 'nonrepresentable-point'].includes(old.kind)
  )
    return {
      passed: false,
      reason: 'The original record is not a checked counterexample certificate.',
    }
  // These local regressions support constraint-strengthening repairs on the same
  // coordinates. A changed encoder needs a separately reviewed translation.
  const sameCoordinates =
    'system' in old &&
    'system' in updated &&
    canonicalSemanticJson(old.system.variables) === canonicalSemanticJson(updated.system?.variables)
  const retainsConstraints =
    sameCoordinates &&
    'system' in old &&
    'system' in updated &&
    Array.isArray(updated.system.constraints) &&
    old.system.constraints.every((c) =>
      updated.system.constraints.some((n) => canonicalSemanticJson(c) === canonicalSemanticJson(n)),
    )
  if (!retainsConstraints)
    return {
      passed: false,
      reason: 'A strengthening regression must preserve the original coordinates and constraints.',
    }
  if (run.kind === 'preserve-valid-case') {
    const sameCase =
      old.kind === 'boolean-feasible' &&
      updated.kind === 'boolean-feasible' &&
      canonicalSemanticJson(old.formula) === canonicalSemanticJson(updated.formula) &&
      canonicalSemanticJson(old.assignment) === canonicalSemanticJson(updated.assignment)
    return {
      passed: sameCase && result.valid,
      reason:
        sameCase && result.valid
          ? 'The original Boolean assignment satisfies the original formula and the strengthened relaxation.'
          : 'The original valid assignment must satisfy both the unchanged formula and the strengthened constraints.',
    }
  }
  const samePoint =
    'point' in old &&
    updated.kind === 'linear-feasible' &&
    canonicalSemanticJson(old.point) === canonicalSemanticJson(updated.point)
  return {
    passed: samePoint && result.failure === 'violation',
    reason:
      samePoint && result.failure === 'violation'
        ? 'The strengthened constraints exclude the original exact point.'
        : 'A malformed input or a different point does not show that this repair excludes the counterexample.',
  }
}

export function repairIssues(
  state: MathematicalResearch,
  pkg: ConjecturePackage,
  context: MathContext = {},
): string[] {
  if (!pkg.repair) return []
  const issues: string[] = []
  if (
    context.artifactRevisions &&
    pkg.artifacts.some((r) => context.artifactRevisions![r.id] !== r.revision)
  )
    issues.push('A repair input artifact changed or is unavailable.')
  if (!pkg.parent || !revisionOf(state.packages, pkg.parent))
    issues.push('The original conjecture package is missing.')
  const dependency = mathematicalDependencyIssue(
    state,
    [pkg.repair.preservation, pkg.repair.complexity],
    context,
  )
  if (dependency) issues.push(dependency)
  const runs = state.regressions.filter((r) => sameMathRef(r.package, pkg))
  for (const id of pkg.repair.defectEvidenceIds) {
    const latest = runs.findLast(
      (r) => r.kind === 'exclude-counterexample' && r.defectEvidenceId === id,
    )
    if (!latest || !checkMathRegression(state, latest).passed)
      issues.push(`No passing exact regression for defect ${id}.`)
  }
  const controls = state.evidence.filter(
    (e) =>
      e.kind === 'exact-certificate' &&
      sameMathRef(e.package, pkg.parent) &&
      checkMathCertificate(e.certificate).valid &&
      (e.certificate as MathCertificate).kind === 'boolean-feasible',
  )
  if (!controls.length) issues.push('No retained valid Boolean control survives this repair.')
  const exclusions = runs.filter(
    (r) => r.kind === 'exclude-counterexample' && checkMathRegression(state, r).passed,
  )
  for (const control of controls) {
    const latest = runs.findLast(
      (r) => r.kind === 'preserve-valid-case' && r.defectEvidenceId === control.id,
    )
    const system = latest?.certificate as MathCertificate | undefined
    if (
      !latest ||
      !checkMathRegression(state, latest).passed ||
      !system ||
      !('system' in system) ||
      !exclusions.some(
        (r) =>
          canonicalSemanticJson((r.certificate as { system: unknown }).system) ===
          canonicalSemanticJson(system.system),
      )
    )
      issues.push(
        `Valid control ${control.id} has not survived the same strengthened constraints as the counterexample regression.`,
      )
  }

  return issues
}

/** A change appends history; it cannot overwrite a checked statement in place. */
export function appendMathematicalRecord<
  K extends
    | 'packages'
    | 'obligations'
    | 'theorems'
    | 'plans'
    | 'relationships'
    | 'evidence'
    | 'regressions',
>(
  state: MathematicalResearch,
  collection: K,
  value: MathematicalResearch[K][number],
): MathematicalResearch {
  const next = parseMathematicalResearch({ ...state, [collection]: [...state[collection], value] })
  if (!next)
    throw new Error(
      'The mathematical record is malformed, duplicated or exceeds its retention bound.',
    )
  return next
}
